<template>
    <b-modal
        :visible="show"
        @change="show = $event"
        size="lg"
        centered
        scrollable
        no-close-on-backdrop
        no-close-on-esc
        :title="$t('ClientContracts')">
        <b-container fluid class="p-0">
            <!-- LISTE DES CONTRATS LIES AU CLIENT -->
            <b-table
                :items="clientContracts"
                :fields="fields"
                :thead-class="'bg-' + Variant + ' text-white'"
                small
                outlined
                responsive
                show-empty
                tbody-tr-class="align-middle"
                :empty-text="$t('NoResult')">
                <template #cell(Delete)="row">
                    <!-- BOUTON DE SUPPRESSION DU CONTRAT -->
                    <b-button
                        @click="deleteContract(row.item)"
                        :variant="'outline-' + Variant"
                        class="btn-circle border-0 ml-1"
                        pill
                        v-b-tooltip.bottom
                        :title="$t('DeleteContract')"
                        size="sm">
                        <font-awesome-icon icon="fa-solid fa-x" />
                    </b-button>
                </template>
                <template #cell(Distributor)="row">
                    {{ forms[row.item.Type]?.Name ?? '-' }}
                </template>
                <!-- BOUTON DE MODIFICATION DU CONTRAT -->
                <template #cell(Edit)="row">
                    <b-button
                        @click="editContract(row.item)"
                        :variant="'outline-' + Variant"
                        class="btn-circle border-0"
                        pill
                        v-b-tooltip.bottom
                        :title="$t('EditContract')"
                        size="sm">
                        <font-awesome-icon icon="fa-solid fa-pen" />
                    </b-button>
                </template>
            </b-table>

            <!-- FORMULAIRE DE CREATION D'UN CONTRAT CLIENT -->
            <b-card no-body class="mt-3">
                <template #header>
                    <h6 class="mb-0">{{ isEditing ? $t('EditContract') : $t('NewClientContract') }}</h6>
                </template>
                <b-card-body>
                    <!-- DISTRIBUTEUR : BOUTONS AVEC LOGO, SEULS CEUX AUTORISANT UN CONTRAT CLIENT (Direct) SONT PROPOSES -->
                    <ValidationProvider rules="required" :name="$t('Distributor')" v-slot="{ errors, valid }" ref="distributorProvider">
                        <b-form-group
                            :label="$t('Distributor')"
                            :state="errors.length > 0 ? false : (valid ? true : null)"
                            :invalid-feedback="errors[0]"
                            class="mb-3">
                            <b-row class="justify-content-center">
                                <b-col
                                    v-for="form in contractDistributors"
                                    :key="form.Type"
                                    class="d-flex mb-3"
                                    cols="12"
                                    sm="6"
                                    lg="4">
                                    <b-button
                                        @click="selectDistributor(form.Type)"
                                        class="distributor-button h-100 w-100 position-relative"
                                        :disabled="isEditing"
                                        :pressed="distributor == form.Type"
                                        :variant="distributor == form.Type ? Variant + '-light bg-white' : 'gray-light bg-white'">
                                        <b-icon-check-circle-fill
                                            v-if="distributor == form.Type"
                                            :variant="Variant"
                                            class="selection-icon"/>
                                        <b-icon-circle
                                            v-else
                                            :variant="Variant"
                                            class="selection-icon"/>
                                        <b-img
                                            :src="getLogo(form.Name)"
                                            :width="300"
                                            alt="Logo"
                                            class="distributor-logo"
                                            v-b-tooltip.html
                                            :title="form.Name"/>
                                    </b-button>
                                </b-col>
                            </b-row>
                        </b-form-group>
                    </ValidationProvider>
                    <!-- NOM DU CONTRAT -->
                    <ValidationProvider rules="required|min:3" :name="$t('ClientContractName')" v-slot="{ errors, valid }" ref="nameProvider">
                        <b-form-group
                            :label="$t('ClientContractName')"
                            label-for="Input_ContractName"
                            :state="errors.length > 0 ? false : (valid ? true : null)"
                            :invalid-feedback="errors[0]"
                            class="mb-3">
                            <b-form-input v-model="name" id="Input_ContractName" :placeholder="$t('ClientContractName')"/>
                        </b-form-group>
                    </ValidationProvider>
                    <!-- CHAMPS DU CONTRAT : GENERES DEPUIS L'ARCHITECTURE DECLAREE COTE SERVEUR (SMAAttribute) -->
                    <ValidationProvider
                        v-for="field in contractFields"
                        :key="field.PropertyName"
                        :rules="{ required: field.Required }"
                        :name="$t(field.LabelKey)"
                        v-slot="{ errors, valid }"
                        ref="fieldProviders">
                        <b-form-group
                            :label="$t(field.LabelKey)"
                            :label-for="'Input_' + field.PropertyName"
                            :state="errors.length > 0 ? false : (valid ? true : null)"
                            :invalid-feedback="errors[0]"
                            class="mb-3">
                            <b-input-group>
                                <b-form-input
                                    v-model="fieldValues[field.PropertyName]"
                                    :type="field.Password && !visibleFields[field.PropertyName] ? 'password' : 'text'"
                                    :id="'Input_' + field.PropertyName"
                                    :placeholder="$t(field.PlaceholderKey)"/>
                                <!-- BOUTON AFFICHAGE / MASQUAGE DES CHAMPS SENSIBLES (CLES API, SECRETS) -->
                                <b-input-group-append v-if="field.Password">
                                    <b-button
                                        @click="toggleFieldVisibility(field.PropertyName)"
                                        v-b-tooltip.html
                                        :variant="Variant"
                                        :title="visibleFields[field.PropertyName] ? $t('Hide') : $t('Show')">
                                        <font-awesome-icon icon="fa-solid fa-eye" />
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </b-form-group>
                    </ValidationProvider>
                </b-card-body>
            </b-card>
        </b-container>
        <!-- BOUTON ENREGISTRER REMPLACANT LE CANCEL / OK DE LA FENETRE -->
        <!-- BOUTON ANNULATION DE L EDITION : RETOUR AU MODE CREATION -->
        <template #modal-footer>
            <b-button
                v-if="isEditing"
                @click="cancelEdition"
                :variant="'outline-' + Variant"
                pill
                class="mr-2">
                {{ $t('CancelEdition') }}
            </b-button>
            <b-button @click="save" :variant="Variant" pill :disabled="isSending">
                <span v-if="isSending">
                    <b-spinner small></b-spinner> {{ $t('Send') }}...
                </span>
                <span v-else>
                    {{ $t('Save') }}
                    <font-awesome-icon icon="fa-solid fa-check" class="ml-2"/>
                </span>
            </b-button>
        </template>
    </b-modal>
</template>
<script lang="ts">

    /**
     * Composants Vue js
     */
    import { Component, InjectReactive, PropSync, Vue, Watch } from 'vue-property-decorator';
    import { mixins } from 'vue-class-component';
    import { Getter } from 'vuex-class';

    /**
     * Validateur formulaire
     */
    import { ValidationProvider } from 'vee-validate';

    /**
     * Classes et énumérations
     */
    import { SMAContract, SMADistributorForm } from '@/types/Administration/SMA/SMADistributorForm';
    import Dictionary from '@/types/Dictionary';
    import ContractTypeEnum from '@/enums/Administration/SMA/ContractTypeEnum';

    /**
     * Utilitaire
     */
    import { SMAUtils } from './SMAUtils';

    /**
     * Traductions
     */
    import locale from '../locales/SMA.json';

    @Component({
        components: { ValidationProvider },
        i18n: { messages: locale }
    })
    /**
     * Composant de gestion des contrats client : liste et création
     * Seuls les contrats liés au client (Direct) sont listés
     * Le formulaire de création est généré à partir de l'architecture déclarée côté serveur (SMAAttribute)
     */
    export default class ClientContractForm extends mixins(Vue, SMAUtils) {

        /**
         * Affichage de la fenêtre
         */
        @PropSync('Show', { default: false })
        private show!: boolean;

        /**
         * Variant de couleur à appliquer sur le composant
         */
        @InjectReactive()
        private Variant!: string;

        /**
         * Liste des contrats du client
         */
        @Getter('CertificateStore/GetContracts')
        private contracts!: Array<SMAContract>;

        /**
         * Liste des formulaires distributeurs
         */
        @Getter('CertificateStore/GetForms')
        private forms!: Dictionary<SMADistributorForm>;

        /**
         * Enumérations
         */
        private contractTypeEnum = ContractTypeEnum;

        /**
         * Colonnes du tableau des contrats
         */
        private fields: Array<any> = [
            { key: 'Delete', label: '', class: 'text-center align-middle', thStyle: { width: '90px' } },
            { key: 'Name', label: this.$t('ClientContractName').toString(), sortable: true, class: 'text-center align-middle' },
            { key: 'Distributor', label: this.$t('Distributor').toString(), class: 'text-center align-middle' },
            { key: 'Edit', label: '', class: 'text-center align-middle', thStyle: { width: '90px' } }
        ];

        /**
         * Identifiant du contrat en cours d'édition (null : création)
         */
        private editingContractId: string | null = null;

        /**
         * Distributeur sélectionné pour la création du contrat
         */
        private distributor: number = null;

        /**
         * Nom du contrat
         */
        private name: string = "";

        /**
         * Valeurs saisies pour les champs du contrat déclarés côté serveur
         */
        private fieldValues: Dictionary<string> = {};

        /**
         * Champs sensibles actuellement affichés en clair (clé : nom de la propriété)
         */
        private visibleFields: Dictionary<boolean> = {};

        /**
         * Indicateur d'envoi vers le serveur
         */
        private isSending: boolean = false;

        /**
         * Contrats liés au client : uniquement les contrats de type Direct
         * Les contrats Tiers (AKEA) ne sont pas liés à une base client
         */
        private get clientContracts(): Array<SMAContract>
        {
            return this.contracts.filter(contract => contract.ContractType == ContractTypeEnum.Direct);
        }

        /**
         * Indique si un contrat existant est en cours d'édition
         */
        private get isEditing(): boolean
        {
            return this.editingContractId != null;
        }

        /**
         * Formulaire distributeur sélectionné
         */
        private get selectedForm(): SMADistributorForm | null
        {
            return this.forms[this.distributor] ?? null;
        }

        /**
         * Distributeurs pour lesquels un contrat client (Direct) est possible
         */
        private get contractDistributors(): Array<SMADistributorForm>
        {
            return Object.values(this.forms)
                .filter(form => form.RequiredContractTypes?.includes(ContractTypeEnum.Direct));
        }

        /**
         * Sélection du distributeur pour le contrat
         * @param type Type distributeur
         */
        private selectDistributor(type: number): void
        {
            this.distributor = type;
        }

        /**
         * Retourne l'url du logo du distributeur : en svg si disponible, sinon en webp
         * @param name Nom du distributeur
         */
        private getLogo(name: string) : string
        {
            try
            {
                return require(`@/assets/images/${name}.svg`);
            }
            catch
            {
                return require(`@/assets/images/${name}.webp`);
            }
        }

        /**
         * Champs du contrat client pour le distributeur sélectionné
         * Architecture déclarée côté serveur dans SMAAttribute
         */
        private get contractFields(): Array<any>
        {
            return this.selectedForm?.ClientContractFields ?? [];
        }

        /**
         * Chargement d'un contrat dans le formulaire pour modification
         * Le distributeur n'est pas modifiable : seuls le nom et les champs du contrat le sont
         * @param contract Contrat à modifier
         */
        private editContract(contract: SMAContract): void
        {
            this.editingContractId = contract.ContractId;
            this.distributor = contract.Type;
            this.name = contract.Name;

            // Alimentation des champs depuis les informations du contrat
            const contractInfo = (contract as any)["ContractInfo"] ?? {};
            this.fieldValues = {};

            for (const field of (this.forms[contract.Type]?.ClientContractFields ?? []))
                this.$set(this.fieldValues, field.PropertyName, contractInfo[field.PropertyName] ?? "");
        }

        /**
         * Suppression d'un contrat après confirmation
         * Un contrat utilisé par un mandat ne peut pas être supprimé (contrôle serveur)
         * @param contract Contrat à supprimer
         */
        private async deleteContract(contract: SMAContract): Promise<void>
        {
            const confirmed = await this.showBoxConfirm(this.$t('PleaseConfirm').toString(), this.Variant, 'DeleteContractConfirm');

            if (!confirmed)
                return;

            const result = await this.$store.dispatch('CertificateStore/DeleteClientContract', {
                ContractId: contract.ContractId
            });

            if (result?.success)
            {
                // Rechargement des formulaires et contrats du client
                await this.$store.dispatch('CertificateStore/LoadSMAForms');
                this.showToast(result.message, "success", "ClientContracts");
                this.resetForm();
            }
            else
            {
                this.showToast(result?.message ?? this.$t('UnknownError').toString(), "danger", "ClientContracts");
            }
        }

        /**
         * Affiche / masque un champ sensible (clé API, secret)
         * @param propertyName Nom de la propriété du champ
         */
        private toggleFieldVisibility(propertyName: string): void
        {
            this.$set(this.visibleFields, propertyName, !this.visibleFields[propertyName]);
        }

        /**
         * Validation de l'ensemble du formulaire via vee-validate
         */
        private async isFormValid(): Promise<boolean>
        {
            const distributorProvider = this.$refs.distributorProvider as any;
            const nameProvider = this.$refs.nameProvider as any;
            const fieldProviders = (Array.isArray(this.$refs.fieldProviders) ? this.$refs.fieldProviders : [this.$refs.fieldProviders]) as Array<any>;

            const results: Array<boolean> = [];

            if (distributorProvider)
            {
                const { valid } = await distributorProvider.validate(this.distributor);
                results.push(valid);
            }

            if (nameProvider)
            {
                const { valid } = await nameProvider.validate(this.name);
                results.push(valid);
            }

            // Les providers sont fournis dans l'ordre du v-for : correspondance avec contractFields
            for (let index = 0; index < fieldProviders.length; index++)
            {
                const field = this.contractFields[index];
                const { valid } = await fieldProviders[index].validate(this.fieldValues[field.PropertyName]);
                results.push(valid);
            }

            return results.every(valid => valid);
        }

        /**
         * Création du contrat client
         */
        private async save(): Promise<void>
        {
            if (!await this.isFormValid())
            {
                this.showToast(this.$t('FormIncomplete').toString(), "warning", "ClientContracts");
                return;
            }

            this.isSending = true;

            const result = await this.$store.dispatch('CertificateStore/SaveClientContract', {
                ContractId: this.editingContractId,
                Name: this.name,
                Type: this.distributor,
                Fields: { ...this.fieldValues }
            });

            if (result?.success)
            {
                // Rechargement des formulaires et contrats du client
                await this.$store.dispatch('CertificateStore/LoadSMAForms');
                this.showToast(result.message, "success", "ClientContracts");
                this.resetForm();
            }
            else
            {
                this.showToast(result?.message ?? this.$t('UnknownError').toString(), "danger", "ClientContracts");
            }

            this.isSending = false;
        }

        /**
         * Annulation de l'édition d'un contrat : retour au mode création
         */
        private cancelEdition(): void
        {
            this.resetForm();
        }

        /**
         * Réinitialisation du formulaire de création
         */
        private resetForm(): void
        {
            this.editingContractId = null;
            this.distributor = null;
            this.name = "";
            this.fieldValues = {};
            this.visibleFields = {};
        }

        /**
         * Réinitialisation des valeurs saisies lors du changement de distributeur
         * @param newVal Distributeur sélectionné
         */
        @Watch('distributor')
        private onDistributorChanged(newVal: number): void
        {
            // Pas de réinitialisation lors du chargement d'un contrat en édition
            if (this.editingContractId != null)
                return;

            this.fieldValues = {};
            this.visibleFields = {};
        }

        /**
         * Réinitialisation du formulaire à la fermeture de la fenêtre
         * @param newVal Nouvel état d'affichage
         */
        @Watch('show')
        private onShowChanged(newVal: boolean): void
        {
            if (!newVal)
                this.resetForm();
        }
    }
</script>
<style scoped>
    /**
     * Icône de sélection positionnée dans le coin haut droit du bouton
     */
    .selection-icon {
        position: absolute;
        top: 5px;
        right: 5px;
    }

    /**
     * Logo du distributeur : s'adapte à la largeur du bouton
     */
    .distributor-logo {
        max-width: 100%;
    }
    /**
     * Coins haut gauche et haut droite arrondis de l'en-tête du tableau des contrats
     * Le rayon s'applique au thead (arrière-plan du variant) et aux cellules (bordures)
     */
    ::v-deep thead {
        border-radius: 8px 8px 0 0;
    }

    ::v-deep thead th:first-child {
        border-top-left-radius: 8px;
    }

    ::v-deep thead th:last-child {
        border-top-right-radius: 8px;
    }
</style>